import { useEffect, useState } from "react";
import { GridMetaData, createUrlSearchParams, filtersEqual, sortsEqual } from "models/grids";
import { ProjectStatus } from "models/projects";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { IMyWorkItem } from "models/work-items/IMyWorkItem";

type GetWorkItemsResult = {
    workItems: IMyWorkItem[],
    total: number
};

type WorkItemsResult = [
    IMyWorkItem[],
    number,
    boolean,
    string | null
];

type UseMyWorkItemsSignature = 
(
    all: boolean,
    status: ProjectStatus,
    gridMetadata: GridMetaData,
    dependenciesLoaded: boolean,
    updateFromDelete?: boolean
)  => WorkItemsResult;

const useMyWorkItems: UseMyWorkItemsSignature = (all, status, gridMetadata, dependenciesLoaded, updateFromDelete) => {
    
    const [ _lastQuery, _setLastQuery ] = useState<string>('');
    const [ result, setResult ] = useState<IMyWorkItem[]>([]);
    const [ total, setTotal ] = useState<number>(0);
    const [ error, setError ] = useState<string | null>(null);
    const [ loading, setLoading ] = useState<boolean>(true);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {        
        const searchParams = createUrlSearchParams(gridMetadata);
        if (all) {
            searchParams.append('all', 'true');
        }

        const query = `${status}?${searchParams}`;

        if (_lastQuery === query && !updateFromDelete) return;
        const controller = new AbortController();
        const fetchWorkItems = async () => {
            if ((user && user.id_token && dependenciesLoaded) || (updateFromDelete && dependenciesLoaded)) {
                setLoading(true);
                
                try {
                    const resp = await fetcher(`/api/v3/workitems/${query}`, {
                        signal: controller.signal
                    });
                    
                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const data = await resp.json() as GetWorkItemsResult;
                    console.log(data);
                    setResult(data.workItems);
                    
                    if (data.total !== total) {
                        setTotal(data.total);
                    }                    
                    _setLastQuery(query);
                } catch (err) {
                    console.log(err);
                    
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            }
        };

        fetchWorkItems();
        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [ gridMetadata, user, status, all, dependenciesLoaded, updateFromDelete ]);

    return [
        result,
        total,
        loading,
        error
    ];
};

export default useMyWorkItems;